/**
 * Saved & watching.
 *
 * A heart on every product card and on the product page, a page listing what
 * was kept, and — the part that makes it worth keeping — what has happened to
 * the price since.
 *
 * Signed out, the list lives in this browser. Signing in folds it into the
 * account rather than replacing either side: somebody who saved three things
 * before signing in and two after has five. Making people sign in before they
 * can save anything loses the save, and usually the customer with it.
 *
 * The set of saved ids is fetched once per page and kept in memory. Hearts
 * render from it, so a card added by an infinite scroll gets the right state
 * without another request.
 */
(function () {
  'use strict';

  var GUEST_KEY = 'tc_saved_v1';
  var API = '/apps/tru/saved';

  var signedIn = null;   // unknown until the first reply
  var ids = null;        // Set of saved product ids
  var ready = null;      // promise for the first load

  /* ---- storage for people who have not signed in --------------------- */
  function guestList() {
    try { return JSON.parse(localStorage.getItem(GUEST_KEY) || '[]'); } catch (e) { return []; }
  }
  function guestWrite(items) {
    try { localStorage.setItem(GUEST_KEY, JSON.stringify(items.slice(0, 200))); } catch (e) {}
  }

  function api(method, path, body) {
    return fetch(API + (path || ''), {
      method: method,
      credentials: 'same-origin',
      headers: { Accept: 'application/json', 'Content-Type': 'application/json' },
      body: body ? JSON.stringify(body) : undefined,
    }).then(function (r) {
      if (r.status === 401) { signedIn = false; throw new Error('signed out'); }
      signedIn = true;
      if (!r.ok) throw new Error(String(r.status));
      return r.json();
    });
  }

  function load() {
    if (ready) return ready;
    ready = api('GET')
      .then(function (data) {
        ids = new Set((data.items || []).map(function (i) { return i.productId; }));
        /* Anything saved before signing in comes with them, once. */
        var waiting = guestList();
        if (waiting.length) {
          return api('POST', '/merge', { items: waiting }).then(function () {
            waiting.forEach(function (i) { ids.add(String(i.productId)); });
            guestWrite([]);
            return data;
          }, function () { return data; });
        }
        return data;
      })
      .catch(function () {
        signedIn = false;
        ids = new Set(guestList().map(function (i) { return String(i.productId); }));
      });
    return ready;
  }

  function isSaved(id) { return !!ids && ids.has(String(id)); }

  function save(item) {
    var id = String(item.productId);
    ids.add(id);
    if (signedIn) return api('POST', '', item).catch(function () { ids.delete(id); throw new Error('failed'); });
    var list = guestList().filter(function (i) { return String(i.productId) !== id; });
    list.unshift(item);
    guestWrite(list);
    return Promise.resolve();
  }

  function unsave(id, listId) {
    id = String(id);
    if (!listId) ids.delete(id);
    if (signedIn) {
      return api('DELETE', '/' + encodeURIComponent(id) + (listId ? '?list=' + encodeURIComponent(listId) : ''))
        .catch(function () { ids.add(id); throw new Error('failed'); });
    }
    guestWrite(guestList().filter(function (i) { return String(i.productId) !== id; }));
    return Promise.resolve();
  }

  /* ---- the heart ------------------------------------------------------ */
  var HEART = '<svg viewBox="0 0 24 24" aria-hidden="true" focusable="false">' +
    '<path d="M12 20.6 4.2 13a4.7 4.7 0 0 1 0-6.7 4.7 4.7 0 0 1 6.7 0l1.1 1.1 1.1-1.1a4.7 4.7 0 0 1 6.7 0 4.7 4.7 0 0 1 0 6.7Z"/></svg>';

  function paint(button, saved) {
    button.setAttribute('aria-pressed', saved ? 'true' : 'false');
    button.setAttribute('aria-label', saved ? tr('saved_remove', 'Saved. Remove from my list') : tr('save_this', 'Save this'));
    button.title = saved ? tr('saved', 'Saved') : tr('save_this', 'Save this');
  }

  function wire(button, item) {
    paint(button, isSaved(item.productId));
    button.addEventListener('click', function (e) {
      e.preventDefault();
      e.stopPropagation();
      var now = !isSaved(item.productId);
      paint(button, now); // answer the press immediately; undo only if it fails
      button.classList.toggle('is-pop', now);
      (now ? save(item) : unsave(item.productId)).catch(function () {
        paint(button, !now);
      });
    });
  }

  function money(text) {
    var m = String(text || '').replace(/,/g, '').match(/(\d+(?:\.\d{1,2})?)/);
    return m ? Number(m[1]) : null;
  }

  /* Product cards. Symmetry gives every card its product id and handle, so
     nothing here depends on the shape of its markup beyond where to hang. */
  function decorateCards(scope) {
    /* Symmetry's own cards and our collection section's cards. Both state their
       product id and handle; they differ only in what wraps the picture. */
    var cards = (scope || document).querySelectorAll(
      '.product-block[data-product-id], .tru-product-card[data-product-id]');
    for (var i = 0; i < cards.length; i++) {
      var card = cards[i];
      var mount = card.querySelector('.image-cont, .tru-product-card__media');
      if (!mount) continue;
      /* Ask the DOM, not a flag on the element. The theme re-renders its own
         card markup after we decorate it, which takes the heart with it — a
         flag then says "done" forever and the card stays bare. */
      if (mount.querySelector('.tc-heart')) continue;

      var img = card.querySelector('img');
      var item = {
        productId: card.getAttribute('data-product-id'),
        handle: card.getAttribute('d-handle') || '',
        title: (card.querySelector('.product-block__title, .tru-product-card__title, .title') || {}).textContent || '',
        image: img ? (img.currentSrc || img.src || '') : '',
        price: money((card.querySelector('.price, .product-price, .tru-product-card__price') || {}).textContent),
        currency: 'GBP',
      };
      if (!item.productId || !item.handle) continue;
      item.title = item.title.trim().slice(0, 255);

      var button = document.createElement('button');
      button.type = 'button';
      button.className = 'tc-heart';
      button.innerHTML = HEART;
      wire(button, item);
      mount.appendChild(button);
    }
  }

  /* The product page states its own facts in markup rather than being parsed. */
  function decorateProduct(scope) {
    var buttons = (scope || document).querySelectorAll('[data-tc-save-btn]');
    for (var i = 0; i < buttons.length; i++) {
      var button = buttons[i];
      if (button.__tcHeart) continue;
      button.__tcHeart = true;
      wire(button, {
        productId: button.getAttribute('data-product-id'),
        handle: button.getAttribute('data-handle'),
        title: button.getAttribute('data-title'),
        image: button.getAttribute('data-image'),
        price: Number(button.getAttribute('data-price')) || null,
        currency: button.getAttribute('data-currency') || 'GBP',
      });
    }
  }

  /* ---- the page ------------------------------------------------------- */
  var L = labelsOf(document.querySelector('[data-tc-saved-page]'));
  /* The words this script says, in the page's language. The section writes
     them into data-labels from the theme's locale files, which locales-sync.js
     fills from the one table the apps and the admin panel share. English
     stays here as the floor, and a key Shopify marks as untranslated falls
     back to it. The translation filter HTML-escapes, so entities are undone. */
  function labelsOf(node) {
    var out = {};
    try {
      var raw = JSON.parse((node && node.getAttribute('data-labels')) || '{}');
      for (var k in raw) if (Object.prototype.hasOwnProperty.call(raw, k) && typeof raw[k] === 'string') {
        out[k] = raw[k].replace(/&lt;/g, '<').replace(/&gt;/g, '>').replace(/&quot;/g, '"').replace(/&#39;/g, "'").replace(/&amp;/g, '&');
      }
    } catch (e) { /* no labels: English */ }
    return out;
  }
  function tr(key, fallback) {
    var v = L[key];
    var out = (typeof v === 'string' && v && v.indexOf('translation missing') !== 0) ? v : fallback;
    for (var i = 2; i < arguments.length; i++) out = out.replace('%s', arguments[i]);
    return out;
  }

  function el(tag, cls, text) {
    var n = document.createElement(tag);
    if (cls) n.className = cls;
    if (text != null) n.textContent = text;
    return n;
  }

  /*
   * Priced in whatever the shopper is buying in.
   *
   * The shop sells into two hundred markets, so a price arrives as an amount and
   * a currency code and both have to be used. A three-symbol lookup put a bare
   * number in front of a Swedish or Swiss shopper, and a pound sign in front of
   * anyone the table did happen to know by the wrong name. Intl knows every code
   * Shopify can return and puts the symbol where the page's own language puts
   * it — "40,00 €" on the French storefront, "€40.00" on the English one.
   *
   * Whole amounts lose their decimals, and only whole ones, so a currency that
   * does not use two places is left as Intl has it.
   */
  function price(amount, currency) {
    var n = parseFloat(amount);
    if (!isFinite(n)) return '';
    var code = currency || 'GBP';
    try {
      var opts = { style: 'currency', currency: code, currencyDisplay: 'narrowSymbol' };
      if (n % 1 === 0) { opts.minimumFractionDigits = 0; opts.maximumFractionDigits = 0; }
      /* The narrow symbol, because the rest of the page uses it: French names
         sterling "£GB" and Spanish "GBP" by default, and a price beside one
         Liquid rendered as "£89.00" should not be wearing a different badge. */
      try {
        return new Intl.NumberFormat(document.documentElement.lang || 'en-GB', opts).format(n);
      } catch (e2) {
        delete opts.currencyDisplay;
        return new Intl.NumberFormat(document.documentElement.lang || 'en-GB', opts).format(n);
      }
    } catch (e) {
      /* An unknown code is still worth naming: a bare number tells a shopper
         nothing, and the wrong symbol tells them something untrue. */
      return code + ' ' + n.toFixed(2);
    }
  }

  function renderPage(root) {
    if (root.__tcSavedPage) return;
    root.__tcSavedPage = true;

    var list = root.querySelector('[data-tc-saved-list]');
    var count = root.querySelector('[data-tc-saved-count]');
    var chips = root.querySelector('[data-tc-lists]');
    var actions = root.querySelector('[data-tc-list-actions]');
    var shopUrl = root.getAttribute('data-shop-url') || '/collections/new-arrivals';
    /* A share link gets pasted into WhatsApp, so it has to be whole. The
       setting holds a path — make it an address. */
    var shareBase = root.getAttribute('data-share-base') || '/pages/list';
    if (!/^https?:/i.test(shareBase)) shareBase = location.origin + shareBase;

    var lists = [];
    var active = new URLSearchParams(location.search).get('list') || '';

    function activeList() {
      for (var i = 0; i < lists.length; i++) if (lists[i].id === active) return lists[i];
      return lists[0] || { id: '', name: tr('saved', 'Saved'), everyday: true };
    }

    /* ---- the lists themselves ---------------------------------------- */
    function drawChips() {
      if (!chips) return;
      chips.innerHTML = '';
      lists.forEach(function (l) {
        var chip = el('button', 'tc-listchip', l.name + (l.count ? ' (' + l.count + ')' : ''));
        chip.type = 'button';
        chip.setAttribute('aria-pressed', l.id === active ? 'true' : 'false');
        chip.addEventListener('click', function () {
          active = l.id;
          /* The address bar keeps up, so a list can be bookmarked and the back
             button behaves. */
          history.replaceState(null, '', l.id ? '?list=' + encodeURIComponent(l.id) : location.pathname);
          refresh();
        });
        chips.appendChild(chip);
      });

      var add = el('button', 'tc-listchip tc-listchip--add', '+ New list');
      add.type = 'button';
      add.addEventListener('click', function () { nameBox(null); });
      chips.appendChild(add);
    }

    /* An inline field rather than prompt(): a prompt blocks the page and reads
       like something the browser did, not something the shop offered. */
    function nameBox(existing) {
      var old = root.querySelector('.tc-namebox');
      if (old) old.remove();
      var box = el('form', 'tc-namebox');
      var input = document.createElement('input');
      input.type = 'text';
      input.maxLength = 80;
      input.placeholder = existing ? tr('rename_list_ph', 'Rename this list') : tr('name_list_ph', 'Name this list — Groom, Ushers, Maybe…');
      input.value = existing ? existing.name : '';
      var go = el('button', 'tc-btn tc-btn--ink', existing ? tr('rename', 'Rename') : tr('create', 'Create'));
      go.type = 'submit';
      var stop = el('button', 'tc-linkish', tr('cancel', 'Cancel'));
      stop.type = 'button';
      stop.addEventListener('click', function () { box.remove(); });
      box.appendChild(input);
      box.appendChild(go);
      box.appendChild(stop);
      box.addEventListener('submit', function (e) {
        e.preventDefault();
        var name = input.value.trim();
        if (!name) return;
        go.disabled = true;
        var request = existing
          ? api('PUT', '/lists/' + encodeURIComponent(existing.id), { name: name })
          : api('POST', '/lists', { name: name });
        request.then(function (made) {
          box.remove();
          if (!existing && made && made.id) {
            active = made.id;
            history.replaceState(null, '', '?list=' + encodeURIComponent(active));
          }
          refresh();
        }, function () { go.disabled = false; });
      });
      chips.parentNode.insertBefore(box, chips.nextSibling);
      input.focus();
    }

    function drawActions() {
      if (!actions) return;
      actions.innerHTML = '';
      var current = activeList();
      if (current.everyday) { actions.hidden = true; return; }
      actions.hidden = false;

      var rename = el('button', 'tc-linkish', tr('rename', 'Rename'));
      rename.type = 'button';
      rename.addEventListener('click', function () { nameBox(current); });
      actions.appendChild(rename);

      var share = el('button', 'tc-linkish', current.shareToken ? tr('stop_sharing', 'Stop sharing') : tr('share_list', 'Share this list'));
      share.type = 'button';
      share.addEventListener('click', function () {
        share.disabled = true;
        api('POST', '/lists/' + encodeURIComponent(current.id) + '/share', { share: !current.shareToken })
          .then(function (out) {
            current.shareToken = out.shareToken;
            drawActions();
          })
          .catch(function () { share.disabled = false; });
      });
      actions.appendChild(share);

      var drop = el('button', 'tc-linkish tc-linkish--warn', tr('delete_list', 'Delete list'));
      drop.type = 'button';
      drop.addEventListener('click', function () {
        if (drop.getAttribute('data-armed') !== 'on') {
          drop.setAttribute('data-armed', 'on');
          drop.textContent = tr('delete_confirm', 'Delete it and everything in it?');
          setTimeout(function () {
            drop.removeAttribute('data-armed');
            drop.textContent = tr('delete_list', 'Delete list');
          }, 5000);
          return;
        }
        drop.disabled = true;
        api('DELETE', '/lists/' + encodeURIComponent(current.id)).then(function () {
          active = '';
          history.replaceState(null, '', location.pathname);
          refresh();
        }, function () { drop.disabled = false; });
      });
      actions.appendChild(drop);

      if (current.shareToken) {
        var url = shareBase + '?id=' + current.shareToken;
        var field = el('div', 'tc-sharebox');
        var link = document.createElement('input');
        link.type = 'text';
        link.readOnly = true;
        link.value = url;
        link.addEventListener('focus', function () { link.select(); });
        var copy = el('button', 'tc-btn tc-btn--ghost-ink', tr('copy_link', 'Copy link'));
        copy.type = 'button';
        copy.addEventListener('click', function () {
          link.select();
          var done = function () { copy.textContent = tr('copied', 'Copied'); setTimeout(function () { copy.textContent = tr('copy_link', 'Copy link'); }, 2000); };
          if (navigator.clipboard) navigator.clipboard.writeText(url).then(done, done);
          else { try { document.execCommand('copy'); done(); } catch (e) {} }
        });
        field.appendChild(link);
        field.appendChild(copy);
        actions.appendChild(field);
      }
    }

    /* ---- the items --------------------------------------------------- */
    function empty(message, cta) {
      list.innerHTML = '';
      var card = el('div', 'tc-card');
      var body = el('div', 'tc-card__body');
      body.appendChild(el('p', 'tc-muted', message));
      if (cta) {
        var a = el('a', 'tc-btn tc-btn--ink', tr('start_looking', 'Start looking'));
        a.href = shopUrl;
        body.appendChild(a);
      }
      card.appendChild(body);
      list.appendChild(card);
    }

    function card(item) {
      var box = el('article', 'tc-saved-card');

      var link = el('a', 'tc-saved-card__img');
      link.href = '/products/' + item.handle;
      if (item.image) {
        var img = new Image();
        img.src = item.image; img.alt = item.title || ''; img.loading = 'lazy';
        link.appendChild(img);
      }
      box.appendChild(link);

      var body = el('div', 'tc-saved-card__body');
      var name = el('a', 'tc-saved-card__t', item.title);
      name.href = '/products/' + item.handle;
      body.appendChild(name);

      var prices = el('div', 'tc-saved-card__p');
      prices.appendChild(el('b', null, price(item.price, item.currency)));
      if (item.drop) prices.appendChild(el('s', null, price(item.savedPrice, item.currency)));
      body.appendChild(prices);
      if (item.drop) {
        /* The point of the list: not "on sale", which everything claims, but
           cheaper than it was for this person since the day they saved it. */
        body.appendChild(el('div', 'tc-saved-card__drop',
          price(item.drop, item.currency) + ' less than when you saved it'));
      }
      if (!item.available) body.appendChild(el('div', 'tc-saved-card__gone', tr('sold_out_now', 'Sold out for now')));

      var row = el('div', 'tc-saved-card__go');
      var open = el('a', 'tc-btn tc-btn--ghost-ink', tr('view', 'View'));
      open.href = '/products/' + item.handle;
      row.appendChild(open);

      var bell = el('button', 'tc-linkish', item.watching ? tr('watching', 'Watching') : tr('watch_price', 'Watch price'));
      bell.type = 'button';
      bell.setAttribute('aria-pressed', item.watching ? 'true' : 'false');
      bell.addEventListener('click', function () {
        var next = bell.getAttribute('aria-pressed') !== 'true';
        bell.disabled = true;
        api('POST', '/' + encodeURIComponent(item.productId) + '/watch', { watching: next, list: active })
          .then(function () {
            bell.setAttribute('aria-pressed', next ? 'true' : 'false');
            bell.textContent = next ? tr('watching', 'Watching') : tr('watch_price', 'Watch price');
          })
          .catch(function () {})
          .then(function () { bell.disabled = false; });
      });
      row.appendChild(bell);

      if (lists.length > 1) {
        var move = document.createElement('select');
        move.className = 'tc-moveto';
        move.setAttribute('aria-label', tr('move_to_list', 'Move to another list'));
        var head = el('option', null, tr('move_to', 'Move to…'));
        head.value = '';
        move.appendChild(head);
        lists.forEach(function (l) {
          if (l.id === active) return;
          var o = el('option', null, l.name);
          o.value = l.id || '__everyday';
          move.appendChild(o);
        });
        move.addEventListener('change', function () {
          if (!move.value) return;
          var to = move.value === '__everyday' ? '' : move.value;
          move.disabled = true;
          api('POST', '/' + encodeURIComponent(item.productId) + '/move', { from: active, to: to })
            .then(function () { refresh(); }, function () { move.disabled = false; });
        });
        row.appendChild(move);
      }

      var drop = el('button', 'tc-linkish tc-linkish--warn', tr('remove', 'Remove'));
      drop.type = 'button';
      drop.addEventListener('click', function () {
        drop.disabled = true;
        unsave(item.productId, active).then(refresh, function () { drop.disabled = false; });
      });
      row.appendChild(drop);

      body.appendChild(row);
      box.appendChild(body);
      return box;
    }

    function draw(data) {
      var items = data.items || [];
      if (count) {
        count.textContent = items.length
          ? tr('n_saved', '%s saved', items.length) + (data.dropped ? ' · ' + tr('n_cheaper', '%s cheaper than when you saved', data.dropped) : '')
          : '';
      }
      if (!items.length) {
        return empty(activeList().everyday
          ? (signedIn === false
              ? tr('empty_browser', 'Nothing saved in this browser yet. Tap the heart on anything you like — sign in and it follows you.')
              : tr('empty_saved', 'Nothing saved yet. Tap the heart on anything you like and it waits for you here.'))
          : tr('empty_list', 'This list is empty. Save something, then move it in here.'), true);
      }
      list.innerHTML = '';
      items.forEach(function (item) { list.appendChild(card(item)); });
    }

    function refresh() {
      if (signedIn === false) {
        var items = guestList().map(function (i) {
          return {
            productId: String(i.productId), handle: i.handle, title: i.title,
            image: i.image, price: i.price, savedPrice: i.price, drop: 0,
            available: true, watching: false, currency: i.currency || 'GBP',
          };
        });
        lists = [{ id: '', name: tr('saved', 'Saved'), count: items.length, everyday: true }];
        drawChips();
        if (actions) actions.hidden = true;
        return draw({ items: items, dropped: 0 });
      }
      return api('GET', active ? '?list=' + encodeURIComponent(active) : '')
        .then(function (data) {
          lists = data.lists || [];
          /* A list deleted in another tab must not leave the page pointing at
             nothing. */
          if (!lists.some(function (l) { return l.id === active; })) active = '';
          drawChips();
          drawActions();
          draw(data);
        }, function () { draw({ items: [] }); });
    }

    load().then(refresh);
  }

  /* ---- boot ----------------------------------------------------------- */
  function boot(scope) {
    var page = (scope || document).querySelector('[data-tc-saved-page]');
    load().then(function () {
      decorateCards(scope);
      decorateProduct(scope);
    });
    if (page) renderPage(page);
  }

  if (document.readyState === 'loading') {
    document.addEventListener('DOMContentLoaded', function () { boot(); });
  } else {
    boot();
  }
  document.addEventListener('shopify:section:load', function (e) { boot(e.target); });

  /* Collections paginate by replacing the grid; catch what arrives later. */
  var watcher = new MutationObserver(function (records) {
    for (var i = 0; i < records.length; i++) {
      if (records[i].addedNodes.length) { decorateCards(document); return; }
    }
  });
  if (document.body) watcher.observe(document.body, { childList: true, subtree: true });
})();
